import { useEffect, useState } from 'react';
import { memberById, unitFor, type M } from '../data/derive';
import { useFamily } from '../data/family';
import { useLensId } from './view';

export interface Lens {
  /** 'all' or a member id */
  id: string;
  member?: M;
  isParent: boolean;
  /** Approvals, kudos, settings: a parent account, or the wall with an active PIN session. */
  canParent: boolean;
  isWall: boolean;
  unit: string;
  /** Signed-in person (not on the wall). */
  me?: M;
}

/** The "lens" is who is looking. It filters and highlights – it never changes the layout. */
export function useLens(): Lens {
  const f = useFamily();
  const stored = useLensId();
  const [now, setNow] = useState(() => Date.now());
  // re-evaluate when a wall parent session runs out
  useEffect(() => {
    if (!f.parentUntil) return;
    const t = window.setTimeout(
      () => setNow(Date.now()),
      Math.max(0, f.parentUntil - Date.now()) + 50,
    );
    return () => window.clearTimeout(t);
  }, [f.parentUntil]);

  const isWall = f.role === 'device';
  const me = memberById(f, f.meId);
  // Kids and teens with their own login always look through their own lens.
  const id = !isWall && me && me.role !== 'parent' ? me.id : stored;
  const member = memberById(f, id);
  const isParent = member?.role === 'parent';
  const canParent = f.role === 'parent' || (isWall && f.parentUntil > now);
  return {
    id: member ? member.id : 'all',
    member,
    isParent,
    canParent,
    isWall,
    unit: unitFor(member),
    me,
  };
}
